import type { EmailOtpType } from '@supabase/supabase-js';

/**
 * Validation for the `/auth/confirm` link parameters.
 *
 * Extracted from the component so the two security decisions here are directly
 * testable rather than only reachable by rendering a page. Both inputs arrive
 * from a query string in an EMAIL, which is the least trustworthy source in the
 * product — the link is written by us but the user clicks whatever they were
 * sent, and anyone can craft a URL to this route.
 */

/**
 * The only verification types that may reach `verifyOtp`.
 *
 * Without an allow-list the caller picks which verification path runs, from a
 * value they control.
 */
const ALLOWED_TYPES = new Set<string>([
  'recovery',
  'invite',
  'signup',
  'magiclink',
  'email',
  'email_change',
]);

export function isAllowedOtpType(raw: string | null): raw is EmailOtpType {
  return raw !== null && ALLOWED_TYPES.has(raw);
}

/**
 * Same-site, path-only redirects. Anything else returns null.
 *
 * Forwarding `redirect_to` unchecked makes this route an open redirect: a
 * genuine auctisync.com link, in a genuine auctisync.com email, that lands the
 * user on an attacker's login page — arriving pre-trusted precisely because the
 * domain they checked was right.
 *
 * `//evil.com` is the case people miss. It starts with `/` but a browser reads
 * it as protocol-relative — an absolute URL to another host — so the leading
 * slash check alone is not enough.
 */
export function safeRedirect(raw: string | null): string | null {
  if (raw === null || raw === '') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  // Backslashes are normalised to forward slashes by some browsers, so `/\evil.com`
  // can escape the origin the same way `//evil.com` does.
  if (raw.startsWith('/\\')) return null;
  return raw;
}
